import { DestroyRef, Injectable, Signal, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DotCMSEditablePageService } from '@dotcms/angular';
import { DotCMSExtendedPageResponse, DotCMSPageRequestParams } from '@dotcms/types';
import { getUVEState } from '@dotcms/uve';
import { Store } from '@ngrx/store';

import { ApiService } from 'ish-core/services/api/api.service';
import { applyConfiguration } from 'ish-core/store/core/configuration';

import { ComposedPageResponse, PageState } from '../../models/dot-cms-uve.model';
import { DotCmsUvePageService } from '../dot-cms-uve-page/dot-cms-uve-page.service';

@Injectable({ providedIn: 'root' })
export class DotCmsUveEditablePageService<T extends DotCMSExtendedPageResponse> {
  cleanUrl = '/';

  constructor(private apiService: ApiService, private store: Store) {}

  getDotCmsUveEditablePage() {
    return this.apiService.get('dot-cms-uve-editable-page');
  }

  #router = inject(Router);
  #pageService = inject(DotCmsUvePageService);
  #dotcmsEditablePageService = inject(DotCMSEditablePageService);
  #destroyRef = inject(DestroyRef);
  //#activatedRoute = inject(ActivatedRoute);

  readonly #context = signal<PageState<T>>({
    status: 'idle',
  });

  /**
   * Initializes the page by loading and managing page content based on the current route.
   * This method:
   * 1. Sets initial loading state
   * 2. Listens for route changes
   * 3. Fetches page content for the current URL
   * 4. Handles vanity URLs and redirects
   * 5. Manages UVE (Universal Visual Editor) integration
   * 6. Updates page state based on responses
   *
   * @param {DotCMSPageRequestParams} extraParams - Additional parameters to include in the page request
   * @returns {Signal<PageState<T>>} A signal containing the current page state
   */
  initializePage(extraParams: DotCMSPageRequestParams = {}, url?: string): Signal<PageState<T>> {
    this.#setLoading();

    if (!url) {
      console.error('No URL passed to initializePage!');
      return this.#context.asReadonly();
    }

    let cleanUrl = url;

    if (cleanUrl.includes('/home') || cleanUrl.includes('/index')) {
      cleanUrl = '/';
    }

    if (cleanUrl.startsWith('/page/')) {
      cleanUrl = cleanUrl.replace('/page', '');
    } else {
      cleanUrl = '/';
    }

    const pageRequest$ = /\/blog\/post\//.test(cleanUrl)
      ? this.#pageService.getBlogDetailPageAsset(cleanUrl, extraParams)
      : this.#pageService.getPageAsset<T>(cleanUrl, extraParams);
    pageRequest$.subscribe(response => {
      const vanityUrl = response.pageAsset?.vanityUrl;
      const action = vanityUrl?.action ?? 0;

      if (action > 200) {
        this.#router.navigate([vanityUrl?.forwardTo]);
        return;
      }

      if (response.error && !getUVEState()) {
        this.#setError({
          message: response.error.message,
          status: 'Error',
        });
        return;
      }

      // If UVE is not enabled, set the page content
      if (!getUVEState()) {
        this.#setPageContent(response as ComposedPageResponse<T>);

        return;
      }
      // If UVE is enabled, listen for changes
      this.#dotcmsEditablePageService
        .listen(response)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(page => {
          // Set the page content every time it changes
          this.store.dispatch(
            applyConfiguration({
              lang: `${page.pageAsset.viewAs.language.languageCode}_${page.pageAsset.viewAs.language.countryCode}`,
            })
          );
          this.#setPageContent(page as ComposedPageResponse<T>);
        });
    });

    return this.#context.asReadonly();
  }

  /**
   * Set the page content
   * @param page
   */
  #setPageContent(page?: ComposedPageResponse<T>) {
    this.#context.set({
      pageResponse: page,
      status: 'success',
      error: undefined,
    });
  }

  /**
   * Set the loading state
   */
  #setLoading() {
    this.#context.update(state => ({
      ...state,
      status: 'loading',
      error: undefined,
    }));
  }

  /**
   * Set the error state
   * @param error
   */
  #setError(error: PageState<T>['error']) {
    this.#context.update(state => ({
      ...state,
      error,
      status: 'error',
    }));
  }
}
